import {type FeedbackRef, frownIcon, frustratedIcon, grinIcon, smileIcon, UserFeedback} from '@github-ui/user-feedback'
import {Button} from '@primer/react'
import {type RefObject, useImperativeHandle, useRef} from 'react'

import {type Rating, useSubmitFeedbackMutation} from './use-submit-feedback-mutation'

const ratingOptions = [
  {
    name: 'Hate it',
    value: 'VERY_NEGATIVE' as const,
    icon: frustratedIcon,
    color: 'veryDissatisfied' as const,
  },
  {
    name: 'Not great',
    value: 'NEGATIVE' as const,
    icon: frownIcon,
    color: 'dissatisfied' as const,
  },
  {
    name: 'It’s ok',
    value: 'POSITIVE' as const,
    icon: smileIcon,
    color: 'satisfied' as const,
  },
  {
    name: 'Love it',
    value: 'VERY_POSITIVE' as const,
    icon: grinIcon,
    color: 'verySatisfied' as const,
  },
]

export type FeedbackDialogRef = FeedbackRef<Rating>

export interface FeedbackDialogProps {
  title?: string
  subject?: string
  placeholderText?: string
  buttonText?: string
  returnFocusRef?: RefObject<HTMLElement | null>
  /** When true, the default "Give feedback" link is hidden. Use ref to trigger openDialog(). */
  hideButton?: boolean
  ref?: React.Ref<FeedbackDialogRef>
  /** Custom endpoint for feedback submission. Defaults to /feedback. */
  endpoint?: string
}

export function FeedbackDialog({
  title,
  subject,
  placeholderText,
  buttonText,
  returnFocusRef,
  hideButton,
  ref,
  endpoint = '/feedback',
}: FeedbackDialogProps) {
  const feedbackDialogRef = useRef<FeedbackDialogRef>(null)
  const submitFeedbackMutation = useSubmitFeedbackMutation(endpoint)

  useImperativeHandle(ref, () => ({
    openDialog: (initialOptionValue?: Rating) => feedbackDialogRef.current?.openDialog(initialOptionValue),
  }))

  const onSubmit = async (rating: Rating | null, text: string): Promise<string[]> => {
    if (rating == null) {
      return ['Please select a rating.']
    }

    const limit = 2000
    if (text.length > limit) {
      return [`Please keep your feedback within ${limit} characters or less.`]
    }

    const body = {
      subject,
      rating,
      content: text,
    }

    await submitFeedbackMutation.mutateAsync(body)

    return []
  }

  return (
    <>
      {!hideButton && (
        <Button type="button" variant="link" onClick={() => feedbackDialogRef.current?.openDialog()}>
          {buttonText ?? 'Give feedback'}
        </Button>
      )}
      <UserFeedback
        ref={feedbackDialogRef}
        title={title}
        options={ratingOptions}
        placeholderText={placeholderText}
        onSubmit={onSubmit}
        returnFocusRef={returnFocusRef}
      />
    </>
  )
}
